import { ThemeOptions } from "@mui/material";
import { Dispatch, ReactNode } from "react";
import { ConfigResponse } from "./ApiDefinitions";

export declare interface AppState {
	appInitialized: boolean;
	reportMenu: Array<any>;
	enc?: string;
	report_configuration: Array<ReportConfiguration>;
	info: ReportInfo;
	owner_adjustment_types: Array<OwnerAdjustment>;
	footerDisclaimer: string;
	disclaimer: string;
	trackView: boolean;
	showErrorPage: boolean;
	theme: ThemeOptions;
	drawerOpen: boolean;
	isDarkMode: boolean;
	currentAddrId?: number;
	isFetchingAdjustments?: boolean;
	selectedCard: CardId;
	colorPallete: AgentColorPallete;
	last_updated?: string;
}
export declare interface AgentColorPallete {
	scheme1: string;
	scheme2: string;
	scheme3: string;
	scheme4: string;
	scheme5: string;
}
export declare interface ReportInfo {
	agent: AgentInfo;
	client: ClientInfo;
	mortgage: MortgageInfo;
	// Mortgage Insights (Canada, switched on per account). Missing or enabled false means off.
	mortgageInsights?: MortgageInsights;
	// The AI Assistant chat on the report, switched on per account. Missing means off.
	aiAssistant?: { enabled: boolean };
}
export declare interface MortgageInsights {
	enabled: boolean;
	// Keyed by address id (addrId), as a string because it comes from JSON.
	addresses?: Record<string, MortgageAddress>;
	// Whether to offer the "add your mortgage" form on its own, and for which addresses.
	prompt?: { show: boolean; addrIds: number[] };
}
export declare interface MortgageAddress {
	purchaseYear: number | null;
	purchasePrice: number | null;
	mortgage: HomeownerMortgage | null;
}
// What the homeowner told us, projected to today by the server.
export declare interface HomeownerMortgage {
	lender: string | null;
	balance: number;
	balanceAsOf: string;
	rate: number;
	rateType: "fixed" | "variable";
	termEnd: string | null; // "YYYY-MM"
	amortizationYears: number;
	updatedAt: string;
	stale: boolean;
	balanceToday: number;
	payment: number;
	paymentEntered: boolean;
	daysToRenewal: number | null;
	renewalWindowOpen: boolean;
	termEnded: boolean;
	history: Array<ChartEntry>;
}
export declare interface ClientInfo {
	name: string;
}
export declare interface MortgageInfo {
	rate_type: string;
	lender_name: string;
	rate: number;
	mortgage_min: number;
	mortgage_max: number;
	term: number;
	lender_legal_name: string;
}
export declare interface AgentInfo {
	account: string;
	agentName: string;
	agent_phone_no: string;
	agent_photo: string;
	businessName: string;
	businessTitle: string;
	email: string;
	messageToClient: string;
	scheduleLink: string;
	signature: string;

	displayOCLogo: boolean;
	displayValuationRange: boolean;
	displayMonthlyChange: boolean;
	displayGainLoss: boolean;
	displayAnnualROI: boolean;
	valuationChartType: "AREA" | "BAR";
	// CLASSIC is the original report, MODERN the redesigned one. Missing means CLASSIC.
	reportLayout?: "CLASSIC" | "MODERN";
	customOCLogo?: string;
	customMortgageProvider?: string;
	mortgageButtonText?: string;
	mortgageRateText?: string;
}

export declare interface ReportConfiguration {
	addrId: number;
	city: string;
	state?: string;
	firstName: string;
	lastName: string;
	owner_adjustments: Array<OwnerAdjustment>;
	street: string;
	reportData: ReportData;
}
export declare interface OwnerAdjustment {
	adjustment_type_id: number;
	adjustment_type: string;
	adjustment_value: number;
	adjustment_reason: string;
	addrid: number;
}
export declare interface ChartEntry {
	x: string;
	y: number;
}
export declare interface ReportData {
	estimatedValue: number;
	lowerRange: number;
	upperRange: number;
	monthlyChange: number;
	gainLoss: number;
	annualROI: number;
	data: Array<ChartEntry>;
}
export declare type APPContext = {
	appState: AppState;
	appDispatch: Dispatch<AppAction>;
};
export declare type AppAction =
	| { type: "INIT"; payload: any }
	| { type: "UPDATE_STATE"; key: string; value: any }
	| { type: "TOGGLE_DRAWER" }
	| { type: "TOGGLE_DARK_MODE" }
	| { type: "SET_CURRENT_ADDR_ID"; value: number }
	| { type: "SET_FETCHING_ADJUSTMENTS"; value: boolean }
	| { type: "SET_SELECTED_CARD"; id: CardId };

export enum CardId {
	CURRENTVALUE = "CURRENTVALUE",
	UPPERRANGE = "UPPER",
	LOWERRANGE = "LOWER",
	NONE = "NONE",
	MONTHLYCHANGE = "MONTHLYCHANGE",
	GAINLOSS = "GAINLOSS",
	ROI = "ROI",
	MORTGAGE = "MORTGAGE",
}
